<template>
    <div ref="editorRef"></div>
</template>

<script lang="ts">
    import { defineComponent, toRaw } from 'vue'
    import { deltaBuilder } from '@/utils/deltaBuilder'
    import Quill from 'quill'
    import Delta from 'quill-delta'

    const IgnoreKeysBindings = {
        enter: {
            key: 'Enter',
            handler: function () {
                return;
            }
        },
        tab: {
            key: 9,
            handler: function () {
                return;
            },
        },
    }

    export default defineComponent({
        props: {
            modelValue: {
                type: Object,
                defaultValue: {
                    text: '',
                    delta: {}
                }
            },
            readOnly: {
                type: Boolean,
                defaultValue: false
            },
            setFocus: {
                type: Boolean,
                defaultValue: false
            },
            isFocused: {
                type: Boolean,
                default: false
            },
            stylesApplied: {
                type: Boolean,
                default: true
            },
            disableFormatting: {
                type: Boolean,
                default: false
            }
        },
        emits: [
            'update:modelValue',
            'textChange',
            'editing',
            'focusEvent'
        ],
        watch: {
            modelValue: {
                handler() {
                    if (!this.editorChangedValue) {
                        this.setEditorValue();

                        if (this.isFocused) {
                            this.cursorToEndOfLine();
                        }
                    }
                },
            },
            setFocus() {
                if (!this.editorIsFocused) {
                    this.cursorToEndOfLine();
                }
            }
        },
        data() {
            return {
                editorChangedValue: false,
                editorIsFocused: false,
                editor: null as (Quill | null),
                emptyDelta: deltaBuilder.createEmptyDelta(),
            };
        },
        computed: {
            textData: {
                get(): any { return this.modelValue },
                set(value: any) { this.$emit('update:modelValue', value) }
            },
            quillEditor(): any {
                return toRaw(this.editor);
            },
            editorOptions(): any {
                if (this.stylesApplied === false && !this.disableFormatting) {
                    return {
                        modules: {
                            toolbar: [
                                ['bold', 'italic', 'underline'],
                                ['link'],
                                ['clean']
                            ],
                            keyboard: {
                                bindings: IgnoreKeysBindings
                            }
                        },
                        theme: 'bubble',
                        readOnly: this.readOnly,
                    };
                }

                return {
                    modules: {
                        toolbar: false,
                        keyboard: {
                            bindings: IgnoreKeysBindings
                        }
                    },
                    theme: undefined,
                    readOnly: this.readOnly,
                };
            }
        },
        mounted() {
            this.editor = new Quill(
                (this.$refs as any).editorRef,
                this.editorOptions
            );

            this.setEditorValue();

            this.editor.on('text-change', () => this.texteditorChangedValue());
            this.editor.on('selection-change',
                (range: any, oldRange: any) =>
                    this.handleBlur(range, oldRange));

            this.editor.root.addEventListener('paste', (e: ClipboardEvent) => {
                e.preventDefault();
                e.stopImmediatePropagation();

                const text = (e.clipboardData?.getData('text/plain') ?? '')
                    .replace(/[\r\n\v\f]+/g, ' ');
                if (!text) {
                    return;
                }

                const editor = this.quillEditor;
                const range = editor?.getSelection() ?? { index: editor?.getLength() ?? 0, length: 0 };

                editor?.insertText(range.index, text, 'user');
                editor?.setSelection(range.index + text.length, 0);
            }, { capture: true });
            this.clearLinks();
        },
        beforeUnmount() {

            if (this.editor == null) {
                return;
            }

            this.editor.off('text-change', () => this.texteditorChangedValue());
            this.editor.off('selection-change',
                (range: any, oldRange: any) =>
                    this.handleBlur(range, oldRange));

            this.editor = null;
        },

        methods: {
            clearLinks() {
                var divs = document.querySelectorAll("input[data-link]");
                [].forEach.call(divs, function (input: HTMLInputElement) {
                    input.dataset.link = "https://www.site.com";
                    input.placeholder = "https://www.site.com";
                });
            },
            setEditorValue() {
                if (this.textData.text != '' && this.deltaIsEmpty(this.textData.delta)) {
                    this.setStringValue(this.textData.text);
                } else {
                    this.setDeltaValue(this.textData.delta as Delta);
                }
            },
            deltaIsEmpty(delta: any) {
                return delta == undefined ||
                    delta == null ||
                    (delta.ops != undefined && delta.ops.length == 0)
            },
            setStringValue(textString: string) {
                var delta = deltaBuilder.createDeltaFromText(textString);

                this.setDeltaValue(delta);
            },
            setDeltaValue(delta: Delta) {
                this.quillEditor?.setContents(delta as Delta, 'silent')
            },
            cursorToEndOfLine() {
                setTimeout(() => {
                    this.quillEditor?.setSelection(this.quillEditor?.getLength(), 0);
                }, 0);
            },
            getPlaneText() {
                return this.quillEditor?.getText().replaceAll("\n", "");
            },
            getDelta() {
                return this.quillEditor?.getContents();
            },
            texteditorChangedValue() {
                this.editorChangedValue = true;

                const changeObject = {
                    text: this.getPlaneText(),
                    delta: this.getDelta()
                }

                this.textData = changeObject;
                this.$emit('editing');
            },
            buildChangeObject() {
                const changeObject = {
                    planeText: this.getPlaneText(),
                    delta: this.getDelta()
                }

                return changeObject;
            },
            handleBlur(range: any, oldRange: any) {
                if (range === null && oldRange !== null) {
                    this.blurAction();
                }
                if (range !== null && oldRange === null) {
                    this.emitFocus();
                }
            },
            blurAction() {
                if (this.editorChangedValue) {
                    const change = this.buildChangeObject();
                    this.$emit('textChange', change);
                    this.editorChangedValue = false;
                }
                this.editorIsFocused = false;
            },
            emitFocus() {
                this.editorIsFocused = true;

                this.$emit('focusEvent');
            },

        }
    })
</script>

<style lang="scss" scoped>
    :deep().ql-container {
        height: auto !important;
    }

    :deep().ql-editor {
        height: auto !important;
        overflow-y: unset;
    }

    :deep().ql-tooltip.ql-editing .ql-tooltip-editor {
        display: flex !important;
    }

    .ql-bubble :deep().ql-tooltip {
        z-index: 9999;
    }
</style>